// @ts-strict-ignore
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { Image, PixelRatio } from 'react-native';
import { FileUpload } from '@shared/components/blocks/types';
import { Signature } from '@shared/data/entity/fields/SignatureField';
import { useSelector } from '@shared/store';
import {
  containedImageUrl,
  fullSizeImageUri,
  greyscaleImageUri,
  jsonImageUri,
} from '@shared/util/media/image';
import { useMountedRef } from './useMountedRef';

interface Props {
  image: FileUpload | MBAPI.ChatAttachment | Signature;
  containerWidth?: number;
  containerHeight?: number;
  size?: 'full' | 'contain';
  grayscale?: boolean;
  heightOffset?: number;
  widthOffset?: number;
}

interface ImageSrcAndDimensions {
  src: string;
  width: number;
  height: number;
  ratio: number;
  isLandscape: boolean;
  isPortrait: boolean;
}

const BASE_DPI = 72;
const dpr = PixelRatio.get();
const SCREEN_BASE_DPI = BASE_DPI * dpr;

/**
 * Given an image object and its container width/height,
 * returns an Imgix src and the width and height of the image.
 *
 * Accounts for current pixel ratio (dpr), and provides fallback width/height
 * when they are not provided in the image object.
 *
 * Will return the fullsize image src or an src for the max image size
 * that will fit in the given container dimensions.
 *
 * Returns ratio, isLandscape, and isPortrait convenience values.
 */
export function useImageSrcAndDimensions({
  image,
  containerWidth,
  containerHeight,
  size = 'full',
  grayscale = false,
  widthOffset = 0,
  heightOffset = 0,
}: Props): ImageSrcAndDimensions {
  const viewportWidth = useSelector(state => state.appState.viewportWidth);
  const viewportHeight = useSelector(state => state.appState.viewportHeight);
  const mounted = useMountedRef();
  const [intrinsicDimensions, setIntrinsicDimensions] = useState([image.width, image.height]);
  const [dimensions, setDimensions] = useState([]);
  const [ppi, setPpi] = useState<number>();
  const ratio = intrinsicDimensions[0] / intrinsicDimensions[1];
  const isLandscape = ratio >= 1;
  const isPortrait = ratio < 1;

  const colorUrl = grayscale ? greyscaleImageUri(image.url) : image.url;
  const src =
    size === 'full'
      ? fullSizeImageUri(colorUrl)
      : containedImageUrl(colorUrl, [containerWidth, containerHeight]);
  const jsonUrl = jsonImageUri(image.url);

  useEffect(() => {
    if (!isNil(ppi)) {
      return;
    }

    if (!jsonUrl) {
      if (mounted.current) {
        setPpi(BASE_DPI);
      }
    }

    fetch(jsonUrl)
      .then(res => {
        return res.json();
      })
      .then(data => {
        if (mounted.current) {
          setPpi(data.DPIWidth ? Math.min(data.DPIWidth, SCREEN_BASE_DPI) : BASE_DPI);
        }
      })
      .catch(() => {
        if (mounted.current) {
          setPpi(BASE_DPI);
        }
      });
  }, [ppi, jsonUrl, setPpi, mounted]);

  useEffect(() => {
    if (!isNil(dimensions[0]) && !isNil(dimensions[1])) {
      return;
    }

    Image.getSize(src, (w, h) => {
      if (mounted.current) {
        setIntrinsicDimensions([w, h]);
      }
    });
  }, [dimensions, src, mounted]);

  useEffect(() => {
    if (isNil(intrinsicDimensions[0]) || isNil(intrinsicDimensions[1])) {
      return;
    }

    if (isNil(ppi)) {
      return;
    }

    const displayDpr = Math.min(dpr, Math.round(dpr / (SCREEN_BASE_DPI / ppi)));
    const maxWidth = !isNil(containerWidth) ? containerWidth : viewportWidth;
    const maxHeight = !isNil(containerHeight) ? containerHeight : viewportHeight;
    const naturalW = intrinsicDimensions[0] / displayDpr;
    const naturalH = intrinsicDimensions[1] / displayDpr;
    // "contain" scaling: use the most restrictive scale so image fits both dimensions
    const scale = Math.min(maxWidth / naturalW, maxHeight / naturalH, 1);
    setDimensions([naturalW * scale + widthOffset, naturalH * scale + heightOffset]);
  }, [
    intrinsicDimensions,
    containerWidth,
    containerHeight,
    widthOffset,
    heightOffset,
    ppi,
    image,
    viewportWidth,
    viewportHeight,
  ]);

  return {
    src,
    width: dimensions[0],
    height: dimensions[1],
    ratio,
    isLandscape,
    isPortrait,
  };
}
